import React from "react";
import { Repeat, IndianRupee, CalendarClock, AlertCircle } from "lucide-react";
import { computeSummary } from "./recurringInvoiceUtils";

const PERIOD_TABS = ["Today", "This week", "This month", "All Time", "Custom Date"];

const PERIOD_MAP = {
  Today: "Today",
  "This week": "Weekly",
  "This month": "Monthly",
  "All Time": "All Time",
  "Custom Date": "Custom Date",
};

function formatCurrency(value) {
  const amount = Number(value) || 0;
  return `₹${amount.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

const MetricCard = ({ label, value, hint, tone = "default", icon: Icon }) => {
  const toneClass =
    tone === "success"
      ? "recurring-summary__value--success"
      : tone === "warn"
        ? "recurring-summary__value--warn"
        : tone === "danger"
          ? "recurring-summary__value--danger"
          : "";

  return (
    <div className="recurring-summary__metric">
      <div className="recurring-summary__metric-head">
        <p className="recurring-summary__metric-label">{label}</p>
        {Icon ? <Icon size={14} aria-hidden /> : null}
      </div>
      <p className={`recurring-summary__value ${toneClass}`}>{value}</p>
      {hint ? <p className="recurring-summary__hint">{hint}</p> : null}
    </div>
  );
};

export default function RecurringInvoicesSummary({
  templates = [],
  period = "All Time",
  onPeriodChange,
}) {
  const filterPeriod = PERIOD_MAP[period] || period;
  const summary = computeSummary(templates, filterPeriod);

  const periodTab =
    typeof period === "string" ? period : period?.type === "Custom Date" ? "Custom Date" : "All Time";

  const [activeTab, setActiveTab] = React.useState(periodTab);
  const [showCustom, setShowCustom] = React.useState(periodTab === "Custom Date");
  const [tempRange, setTempRange] = React.useState({
    from: period?.from || "",
    to: period?.to || "",
  });

  React.useEffect(() => {
    setActiveTab(periodTab);
    if (periodTab !== "Custom Date") setShowCustom(false);
    if (period?.from || period?.to) {
      setTempRange({ from: period.from || "", to: period.to || "" });
    }
  }, [periodTab, period?.from, period?.to]);

  const handleTabClick = (tab) => {
    if (tab === "Custom Date") {
      setActiveTab("Custom Date");
      setShowCustom(true);
      return;
    }
    setActiveTab(tab);
    setShowCustom(false);
    onPeriodChange?.(tab);
  };

  const handleApplyCustom = () => {
    if (!tempRange.from || !tempRange.to || tempRange.from > tempRange.to) return;
    onPeriodChange?.({ type: "Custom Date", from: tempRange.from, to: tempRange.to });
    setShowCustom(false);
  };

  return (
    <section className="recurring-summary" aria-label="Auto-billing summary">
      <div className="recurring-summary__metrics">
        <MetricCard
          label="Active schedules"
          value={summary.activeCount}
          hint="Currently billing"
          tone="success"
          icon={Repeat}
        />
        <MetricCard
          label="Monthly value"
          value={formatCurrency(summary.monthlyValue)}
          hint="Normalized recurring total"
          icon={IndianRupee}
        />
        <MetricCard
          label="Due this week"
          value={`${summary.dueWeekCount} · ${formatCurrency(summary.dueWeekAmount)}`}
          hint="Next 7 days"
          tone="warn"
          icon={CalendarClock}
        />
        <MetricCard
          label="Needs attention"
          value={summary.attentionCount}
          hint="Missed runs or blocked"
          tone={summary.attentionCount > 0 ? "danger" : "default"}
          icon={AlertCircle}
        />
      </div>

      <div className="recurring-summary__period">
        <p className="recurring-summary__period-label">Next run period</p>
        <div className="recurring-summary__period-tabs dash-filter-scroll">
          {PERIOD_TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              className={`recurring-summary__period-tab ${activeTab === tab ? "recurring-summary__period-tab--active" : ""}`}
              onClick={() => handleTabClick(tab)}
            >
              {tab}
            </button>
          ))}
        </div>
        {showCustom && (
          <div className="recurring-summary__custom-range">
            <label>
              From
              <input
                type="date"
                value={tempRange.from}
                onChange={(e) => setTempRange((prev) => ({ ...prev, from: e.target.value }))}
              />
            </label>
            <label>
              To
              <input
                type="date"
                value={tempRange.to}
                onChange={(e) => setTempRange((prev) => ({ ...prev, to: e.target.value }))}
              />
            </label>
            <div className="recurring-summary__custom-actions">
              <button
                type="button"
                className="recurring-summary__custom-cancel"
                onClick={() => {
                  setShowCustom(false);
                  if (typeof period === "string") setActiveTab(period);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="recurring-summary__custom-apply"
                disabled={!tempRange.from || !tempRange.to || tempRange.from > tempRange.to}
                onClick={handleApplyCustom}
              >
                Apply
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
